import React from "react";
import Styles from "./GrowNetwork.module.scss";
import {
    useGnAchievement,
    useGrowFeatures,
    useGrowHowItworkData,
    useStepsHowToMakeBusinessSuccessful,
    useStepsHowToStartBusiness
} from "../service-data/ServiceData";

import { FaqsResponse } from "@vahak/core/dist/_services/faq.service";
import { ga4sendEvent } from "@vahak/core/dist/_services/user-analytics.service";

import A from "@vahak/core/dist/components/app-anchor/A";
import AppBtn from "@vahak/core/dist/components/app-btn/AppBtn";

import Features from "../common/features/Features";
import Achievements from "../common/achievements/Achievements";
import HeroSection from "../common/hero-section/HeroSection";
import HowItWorks from "../common/how-it-works/HowItWorks";
import Faq from "../common/faq/Faq";
import AppImg from "components/utils/AppImg";

import { useTranslation } from "next-i18next";
import ArrowRightWhite from "@vahak/core/dist/icons/arrow-right-white.svg";
import { GA4EventNames, GA4ServicePageGrowNetworkEventValues } from "@vahak/core/dist/constants/event-names";
import { SOURCE_UTM } from "@vahak/core/dist/constants/misc";
import { PageTypes } from "@vahak/core/dist/constants/Pages";
import PageMetaTags from "../../common/page-meta-tags/PageMetaTags";
import { breadcrumbsConstants } from "../../../constants/breadcrumbs-constants";
import ServiceInformation from "../common/service-information/ServiceInformation";
import GenericTitleDescriptionWithParas from "../../generic-title-description-with-paras/GenericTitleDescriptionWithParas";
import Flex from "@vahak/core-ui/dist/layout/Flex";

interface GrowNetworkProps {
    faqs: FaqsResponse[];
}
const metaData = {
    title: "Grow Your Transport Business on India's #1 Transport Community | Vahak",
    description:
        "Grow your Transport Business by establishing trustworthy connections with India's Top transporters through our #1 digital transportation network. Join now!",
    keywords:
        " transport business,how to start a transport business,transport business ideas, how to start a transport business in india, transport business plan,truck transport business in india, how to grow transport business, goods transport business,best transport business in india, how to run transport business,how to expand logistics business, how to expand transport business, how to grow transport business in india, how to make a transport business successful,online transport business,online transport business in india,",
    canonicalUrl: "https://www.vahak.in/grow-network",
    schemaOrgJsonUrl: "/grow-network",
    ogImgSrc: "https://d3ib4ot6oar1ep.cloudfront.net/1080/75/webp/largest-transporter-network-india_desktop.png",
    ogImgAltText: "Transport Business",
    breadcrumbList: breadcrumbsConstants({}).growNetwork
};
const GrowNetwork = ({ faqs }: GrowNetworkProps) => {
    const { t } = useTranslation("servicePages");

    return (
        <>
            <PageMetaTags
                title={metaData.title}
                description={metaData.description}
                keywords={metaData.keywords}
                canonicalUrl={metaData.canonicalUrl}
                ogUrl={metaData.canonicalUrl}
                ogImgSrc={metaData.ogImgSrc}
                ogImgAltText={metaData.ogImgAltText}
                schemaOrgJsonUrl={metaData.schemaOrgJsonUrl}
                breadcrumbList={metaData.breadcrumbList}
            />

            <main className={Styles.main}>
                <HeroSection
                    bannerTitle={t("servicePages:grow-network.hero-section.bannerTitle")}
                    bannerText={t("servicePages:grow-network.hero-section.bannerText")}
                    hrefLabel={t("servicePages:grow-network.hero-section.hrefLabel")}
                    hrefURL={`https://6r7cz.app.link/startnetworking/?${SOURCE_UTM}=${PageTypes.GROW_NETWORK}`}
                    onLinkClick={() => {
                        ga4sendEvent(GA4EventNames.Servicepage_grow_network, {
                            value: GA4ServicePageGrowNetworkEventValues.start_networking
                        });
                    }}
                    // srcSetURL={`${IMG_BASE_URL}/largest-transporter-network-india_desktop_3x.png 1518w, ${IMG_BASE_URL}/largest-transporter-network-india_desktop_2x.png 1012w, ${IMG_BASE_URL}/largest-transporter-network-india_desktop.png 506w, ${IMG_BASE_URL}/largest-transporter-network-india_mobile_3x.png 810w, ${IMG_BASE_URL}/largest-transporter-network-india_mobile_2x.png 540w, ${IMG_BASE_URL}/largest-transporter-network-india_mobile.png 270w`}
                    srcURL={`largest-transporter-network-india_desktop.png`}
                    altText="truck with boxes and India map"
                />

                <Achievements
                    achievementData={useGnAchievement()}
                    sectionTitle={t("servicePages:grow-network.achievements.sectionTitle")}
                    sectionText={t("servicePages:grow-network.achievements.sectionText")}
                />

                <ServiceInformation
                    heading={t("servicePages:grow-network.how-to-start-a-transport-business.heading")}
                    sectionTitle={""}
                    sectionText={t("servicePages:grow-network.how-to-start-a-transport-business.description")}
                    descriptionComponent={
                        <Flex gap={30} flexDirection="column">
                            <GenericTitleDescriptionWithParas
                                titleDescriptionList={useStepsHowToStartBusiness()}
                                heading={""}
                            />
                        </Flex>
                    }
                />

                <ServiceInformation
                    heading={t("servicePages:grow-network.how-to-make-a-transport-business-successful.heading")}
                    sectionTitle={""}
                    sectionText={t("servicePages:grow-network.how-to-make-a-transport-business-successful.description")}
                    descriptionComponent={
                        <Flex gap={30} flexDirection="column">
                            <GenericTitleDescriptionWithParas
                                titleDescriptionList={useStepsHowToMakeBusinessSuccessful()}
                                heading={""}
                            />
                        </Flex>
                    }
                />

                <ServiceInformation
                    heading={t("servicePages:grow-network.grow-your-transport-business-with-vahak.heading")}
                    sectionTitle={""}
                    sectionText={t("servicePages:grow-network.grow-your-transport-business-with-vahak.description")}
                />

                <Features
                    features={useGrowFeatures()}
                    sectionTitle={t("servicePages:grow-network.features.sectionTitle")}
                    sectionText={t("servicePages:grow-network.features.sectionText")}
                    ctaLink="https://6r7cz.app.link/growwithvahak"
                    ctaLabel={t("servicePages:grow-network.features.ctaLabel")}
                    ctaClass={Styles.ctaBg}
                    onCtaClick={() => {
                        ga4sendEvent(GA4EventNames.Servicepage_grow_network, {
                            value: GA4ServicePageGrowNetworkEventValues.grow_network
                        });
                    }}
                />

                <HowItWorks
                    howitwork={useGrowHowItworkData()}
                    sectionTitle={t("servicePages:grow-network.HowItWorks.sectionTitle")}
                    sectionText={t("servicePages:grow-network.HowItWorks.sectionText")}
                    tabAppNav={t("servicePages:online-load-booking.HowItWorks.tabAppNav")}
                    tabWebNav={t("servicePages:online-load-booking.HowItWorks.tabWebNav")}
                    onAppTabChange={() => {
                        ga4sendEvent(GA4EventNames.Servicepage_grow_network, {
                            value: GA4ServicePageGrowNetworkEventValues.mobile_app
                        });
                    }}
                    onWebTabChange={() => {
                        ga4sendEvent(GA4EventNames.Servicepage_grow_network, {
                            value: GA4ServicePageGrowNetworkEventValues.web
                        });
                    }}
                />

                <Faq
                    faqs={faqs}
                    sectionTitle={t("servicePages:online-load-booking.Faq.sectionTitle")}
                    sectionText={t("servicePages:online-load-booking.Faq.sectionText")}
                />

                <section className={Styles.ctaBanner}>
                    <A href="https://6r7cz.app.link/growbusiness" openInNewTab>
                        <div className="container">
                            <div className={Styles.ctarow}>
                                <div className={Styles.imgBox}>
                                    <AppImg
                                        src={`gn_ctaBannerImg.png`}
                                        layout="responsive"
                                        alt="CTABanner"
                                        width="310"
                                        height="153"
                                    />
                                </div>
                                <div className={Styles.textBox}>
                                    <h2>{t("servicePages:grow-network.section-ctaBanner.heading")}</h2>
                                    <AppBtn className={Styles.ctaBtn}>
                                        {t("servicePages:grow-network.section-ctaBanner.button")}
                                        <ArrowRightWhite />
                                    </AppBtn>
                                </div>
                            </div>
                        </div>
                    </A>
                </section>
            </main>
        </>
    );
};

export default GrowNetwork;
